import DateFnsUtils from "@date-io/date-fns";
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  makeStyles,
  TextField,
} from "@material-ui/core";
import { Close, Face, FilterList, Inbox } from "@material-ui/icons";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { format } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdGroups } from "react-icons/md";
import InputMask from "react-input-mask";
import { getInputDateFormats } from "../../../../utils/get-date-time-locale";
import { WrapperChip } from "./styles.js";

import { getAllCommitteesMettings } from "../../../../services/committees.service";
import { getMembersByName } from "../../../../services/formCombos.service";
import {
  bulkUpdateMeetings,
  getAll as getMeetings,
} from "../../../../services/meeting.service";
import { Select } from "../../../Common/Select";
import {
  ActionButtons,
  AttendeesSection,
  CloseButton,
  Container,
  EmptyState,
  FilterSection,
  FormSection,
  Header,
  InitialState,
  LoadingContainer,
  MeetingsTable,
  SelectionSummary,
  TableHeader,
  TableRow,
  TitleSection,
  TruncatedWarning,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 150,
    minHeight: 45,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 150,
    minHeight: 45,
  },
  filterButton: {
    fontSize: "0.875rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 120,
    minHeight: 40,

    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      cursor: "pointer",
    },

    "&.Mui-disabled": {
      backgroundColor: theme.palette.grey.light,
      color: `${theme.palette.primary.contrastText} !important`,
    },
  },
}));

const NON_UPDATABLE_STATUSES = ["C", "UD", "CC"];
const MAX_MEETINGS = 50;

function BulkUpdateMeetingsModal({ onClose, onSuccess }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  // Filter states
  const [committees, setCommittees] = useState([]);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Grid states
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [selectedMeetingIds, setSelectedMeetingIds] = useState([]);
  const [truncated, setTruncated] = useState(false);
  const [totalFound, setTotalFound] = useState(0);

  // Form states
  const [meetStartTime, setMeetStartTime] = useState("");
  const [meetEndTime, setMeetEndTime] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [memberInputValue, setMemberInputValue] = useState("");
  const [memberOptions, setMemberOptions] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Load committees on mount
  useEffect(() => {
    async function loadCommittees() {
      try {
        const res = await getAllCommitteesMettings();
        if (res.data) {
          setCommittees(
            res.data.map((data) => ({
              id: data.committeeId,
              name: data.committeeName,
            }))
          );
        }
      } catch {
        setCommittees([]);
      }
    }
    loadCommittees();
  }, []);

  // Debounced member search
  useEffect(() => {
    if (memberInputValue?.length >= 3) {
      const languageId = localStorage.getItem("i18nextLng") || "pt";
      const timer = setTimeout(async () => {
        const res = await getMembersByName(languageId, memberInputValue);
        if (res.error?.length > 0) {
          setMemberOptions([]);
        } else {
          setMemberOptions(res.data || []);
        }
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setMemberOptions([]);
    }
  }, [memberInputValue]);

  // Pre-populate attendees when selection changes
  useEffect(() => {
    if (meetings.length === 0 || selectedMeetingIds.length === 0) {
      return;
    }

    const selectedMeetings = meetings.filter((m) =>
      selectedMeetingIds.includes(m.id)
    );

    const attendeeMap = new Map();
    selectedMeetings.forEach((meeting) => {
      try {
        const meetAttendees = meeting.attendees;
        meetAttendees.forEach((att) => {
          const key = att.id;
          if (key && !attendeeMap.has(key)) {
            attendeeMap.set(key, {
              id: key,
              name: att.Name || att.name || "",
              email: att.Email || att.email || "",
              isCommitteeMember: att.IsCommitteeMember || att.isCommitteeMember || false,
            });
          }
        });
      } catch {
        // ignore parsing errors
      }
    });

    setAttendees(Array.from(attendeeMap.values()));
  }, [selectedMeetingIds, meetings]);

  const handleSearch = async () => {
    if (!selectedCommittee || !startDate || !endDate) return;

    setLoading(true);
    setHasSearched(true);
    setError("");

    try {
      let allMeetings = [];
      let currentPage = 0;
      let totalPages = 1;
      const pageSize = 100;

      while (currentPage < totalPages) {
        const response = await getMeetings({
          filters: {
            committeeId: selectedCommittee.id,
            startDate: format(startDate, "yyyy-MM-dd"),
            endDate: format(endDate, "yyyy-MM-dd"),
          },
          pageSize,
          currentPage,
        });

        if (response.error?.length > 0) {
          break;
        }

        allMeetings = allMeetings.concat(response.data || []);
        totalPages = response.totalPages || 1;
        currentPage++;
      }

      // Filter out non-updatable statuses and RoundRobin meetings
      const filteredMeetings = allMeetings.filter(
        (m) =>
          !NON_UPDATABLE_STATUSES.includes(m.statusName) && !m.roundRobin
      );

      const isTruncated = filteredMeetings.length > MAX_MEETINGS;
      setTruncated(isTruncated);
      setTotalFound(filteredMeetings.length);

      const displayMeetings = filteredMeetings.slice(0, MAX_MEETINGS);
      setMeetings(displayMeetings);
      setSelectedMeetingIds(displayMeetings.map((m) => m.id));
    } catch {
      setMeetings([]);
      setError(t("meeting.bulkUpdate.allFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMeeting = (meetingId) => {
    const shouldSelect = !selectedMeetingIds.includes(meetingId);
    setSelectedMeetingIds((prev) => {
      const next = new Set(prev);
      if (shouldSelect) {
        next.add(meetingId);
      } else {
        next.delete(meetingId);
      }
      return Array.from(next);
    });
  };

  const handleToggleAll = () => {
    if (selectedMeetingIds.length === meetings.length) {
      setSelectedMeetingIds([]);
    } else {
      setSelectedMeetingIds(meetings.map((m) => m.id));
    }
  };

  const handleAddAttendee = (value) => {
    if (value && !attendees.find((a) => a.id === value.id)) {
      setAttendees((prev) => [
        ...prev,
        {
          id: value.id,
          name: value.name,
          email: value.email,
          isCommitteeMember: false,
        },
      ]);
    }
    setSelectedMember(null);
    setMemberInputValue("");
  };

  const handleRemoveAttendee = ({ id: attendeeId }) => {
    setAttendees((prev) => prev.filter((a) => a.id !== attendeeId));
  };

  const handleSubmit = async () => {
    setError("");

    if (selectedMeetingIds.length === 0) {
      setError(t("meeting.bulkUpdate.noMeetings"));
      return;
    }

    if ((meetStartTime && !meetEndTime) || (!meetStartTime && meetEndTime)) {
      setError(
        t("meeting.bulkUpdate.timeBothRequired") ||
          "Provide both start and end time, or leave both blank to keep each meeting's original time"
      );
      return;
    }

    setSubmitting(true);

    try {
      const normalizeTime = (value) =>
        value ? (value.length === 5 ? `${value}:00` : value) : null;

      const payload = {
        meetingIds: selectedMeetingIds,
        meetStartTime: normalizeTime(meetStartTime),
        meetEndTime: normalizeTime(meetEndTime),
        attendees: attendees.map((a) => ({
          id: a.id,
          name: a.name,
          email: a.email,
          isCommitteeMember: a.isCommitteeMember || false,
        })),
      };

      const response = await bulkUpdateMeetings(payload);

      if (response.error) {
        setError(response.error);
        setSubmitting(false);
        return;
      }

      const data = response.data;
      const successCount = data?.successIds?.length || 0;
      const failCount = data?.failures?.length || 0;
      const total = successCount + failCount;

      if (failCount === 0) {
        onSuccess({
          message: t("meeting.bulkUpdate.allSuccess", { count: successCount }),
          type: "success",
        });
      } else if (successCount === 0) {
        onSuccess({
          message: t("meeting.bulkUpdate.allFailed"),
          type: "error",
        });
      } else {
        onSuccess({
          message: t("meeting.bulkUpdate.partialSuccess", {
            success: successCount,
            total,
            failed: failCount,
          }),
          type: "warning",
        });
      }

      onClose();
    } catch {
      setError(t("meeting.bulkUpdate.allFailed"));
    } finally {
      setSubmitting(false);
    }
  };

  const isAllSelected =
    meetings.length > 0 && selectedMeetingIds.length === meetings.length;
  const isIndeterminate =
    selectedMeetingIds.length > 0 &&
    selectedMeetingIds.length < meetings.length;
  const canSearch = selectedCommittee && startDate && endDate;

  return (
    <Container>
      <Header>
        <TitleSection>
          <h2>{t("meeting.bulkUpdate.title")}</h2>
          <p>{t("meeting.bulkUpdate.subtitle") || ""}</p>
        </TitleSection>
        <CloseButton onClick={onClose}>
          <Close />
        </CloseButton>
      </Header>

      {/* Filter Section */}
      <FilterSection>
        <div className="filter-field">
          <Select
            fullWidth
            id="bulkCommittee"
            options={committees}
            value={selectedCommittee}
            label={t("meeting.bulkUpdate.committee")}
            setValue={(value) => setSelectedCommittee(value)}
          />
        </div>

        <div className="filter-field">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              disableToolbar
              variant="inline"
              format={getInputDateFormats(i18n.language)}    
              label={t("meeting.bulkUpdate.startDate")}
              value={startDate}
              onChange={setStartDate}
              fullWidth
              KeyboardButtonProps={{ "aria-label": "change start date" }}
            />
          </MuiPickersUtilsProvider>
        </div>

        <div className="filter-field">
          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <KeyboardDatePicker
              autoOk
              disableToolbar
              variant="inline"
              format={getInputDateFormats(i18n.language)}    
              label={t("meeting.bulkUpdate.endDate")}
              value={endDate}
              onChange={setEndDate}
              fullWidth
              KeyboardButtonProps={{ "aria-label": "change end date" }}
            />
          </MuiPickersUtilsProvider>
        </div>

        <div className="filter-actions">
          <Button
            className={classes.filterButton}
            startIcon={<FilterList />}
            onClick={handleSearch}
            disabled={loading || !canSearch}
          >
            {t("meeting.bulkUpdate.search")}
          </Button>
        </div>
      </FilterSection>

      {/* Content */}
      {loading ? (
        <LoadingContainer>
          <CircularProgress />
          <p>{t("meeting.bulkUpdate.loading") || "Loading..."}</p>
        </LoadingContainer>
      ) : !hasSearched ? (
        <InitialState>
          <FilterList />
          <p>{t("meeting.bulkUpdate.initialMessage") || "Select a committee and period to search meetings."}</p>
          <span className="hint">
            {t("meeting.bulkUpdate.initialHint") || "All required filters must be filled."}
          </span>
        </InitialState>
      ) : meetings.length === 0 ? (
        <EmptyState>
          <Inbox />
          <p>{t("meeting.bulkUpdate.noMeetings")}</p>
        </EmptyState>
      ) : (
        <>
          {/* Truncated warning */}
          {truncated && (
            <TruncatedWarning>
              {t("meeting.bulkUpdate.limitWarning", {
                shown: MAX_MEETINGS,
                total: totalFound,
              })}
            </TruncatedWarning>
          )}

          {/* Selection summary */}
          <SelectionSummary>
            <span className="count">
              <strong>{selectedMeetingIds.length}</strong> / {meetings.length}{" "}
              {t("meeting.bulkUpdate.selected")}
            </span>
            <button className="select-all-btn" onClick={handleToggleAll}>
              {isAllSelected
                ? t("meeting.bulkUpdate.deselectAll") || "Deselect All"
                : t("meeting.bulkUpdate.selectAll") || "Select All"}
            </button>
          </SelectionSummary>

          {/* Meetings grid */}
          <MeetingsTable>
            <TableHeader>
              <div>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleToggleAll}
                />
              </div>
              <div>{t("meeting.bulkUpdate.committeeName")}</div>
              <div>{t("meeting.bulkUpdate.meetingDate")}</div>
              <div>{t("meeting.bulkUpdate.time")}</div>
              <div>{t("meeting.bulkUpdate.secretary")}</div>
              <div>{t("meeting.bulkUpdate.status")}</div>
            </TableHeader>

            {meetings.map((meeting, index) => (
              <TableRow
                key={meeting.id}
                $index={index}
                $isSelected={selectedMeetingIds.includes(meeting.id)}
              >
                <div>
                  <Checkbox
                    checked={selectedMeetingIds.includes(meeting.id)}
                    onChange={() => handleToggleMeeting(meeting.id)}
                  />
                </div>
                <div>
                  <span title={meeting.committeName}>{meeting.committeName}</span>
                </div>
                <div>{meeting.meetDate}</div>
                <div>
                  {meeting.meetStartTime} - {meeting.meetEndTime}
                </div>
                <div>
                  <span title={meeting.secretaryName}>{meeting.secretaryName}</span>
                </div>
                <div>{meeting.statusName}</div>
              </TableRow>
            ))}
          </MeetingsTable>

          {/* Form Section */}
          <FormSection>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3}>
                <InputMask
                  mask="99:99"
                  maskChar=""
                  value={meetStartTime}
                  onChange={(e) => setMeetStartTime(e.target.value)}
                >
                  {() => (
                    <TextField
                      label={t("meeting.bulkUpdate.startTime")}
                      fullWidth
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid item xs={12} sm={3}>
                <InputMask
                  mask="99:99"
                  maskChar=""
                  value={meetEndTime}
                  onChange={(e) => setMeetEndTime(e.target.value)}
                >
                  {() => (
                    <TextField
                      label={t("meeting.bulkUpdate.endTime")}
                      fullWidth
                    />
                  )}
                </InputMask>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Select
                  fullWidth
                  id="bulkMemberSearch"
                  options={memberOptions}
                  value={selectedMember}
                  onInputChange={(_event, newInputValue) => {
                    setMemberInputValue(newInputValue);
                  }}
                  inputValue={memberInputValue}
                  label={t("meeting.bulkUpdate.addMember")}
                  setValue={(value) => {
                    handleAddAttendee(value);
                  }}
                />
                <AttendeesSection>
                  {attendees.map((attendee) => (
                    <WrapperChip status={""}>
                      <Chip
                        clickable={false}
                        icon={
                          attendee.isCommitteeMember ? (
                            <MdGroups style={{ fontSize: "1rem" }} />
                          ) : (
                            <Face />
                          )
                        }
                        label={attendee.isExternal ? `${attendee.name} (Externo)` : attendee.name}
                        onDelete={() => handleRemoveAttendee(attendee)}
                        color="primary"
                        style={attendee.isExternal ? { borderStyle: "dashed" } : {}}
                      />
                    </WrapperChip>
                  ))}
                </AttendeesSection>
              </Grid>
            </Grid>

          </FormSection>
        </>
      )}

      {error && (
        <p style={{ color: "#E11021", marginTop: 16, fontSize: "0.875rem" }}>
          {error}
        </p>
      )}

      <ActionButtons>
        <Button
          variant="contained"
          onClick={onClose}
          className={classes.buttonOutlined}
          disabled={submitting}
        >
          {t("meeting.bulkUpdate.cancel")}
        </Button>

        <Button
          variant="contained"
          onClick={handleSubmit}
          className={classes.button}
          disabled={submitting || meetings.length === 0}
        >
          {submitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("meeting.bulkUpdate.save")
          )}
        </Button>
      </ActionButtons>
    </Container>
  );
}

export default BulkUpdateMeetingsModal;
